export const SCHEDULE_WEEKDAYS = [
  { id: 1, label: "Пн" },
  { id: 2, label: "Вт" },
  { id: 3, label: "Ср" },
  { id: 4, label: "Чт" },
  { id: 5, label: "Пт" },
  { id: 6, label: "Сб" },
  { id: 0, label: "Вс" },
];

export function minutesToTime(totalMin) {
  const normalized = ((totalMin % (24 * 60)) + 24 * 60) % (24 * 60);
  const h = Math.floor(normalized / 60);
  const m = normalized % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

export function parseTimeToMinutes(time) {
  const [h, m] = String(time || "0:0")
    .slice(0, 5)
    .split(":")
    .map(Number);
  return (h || 0) * 60 + (m || 0);
}

export function addMinutesToTime(time, minutes) {
  return minutesToTime(parseTimeToMinutes(time) + Number(minutes || 0));
}

export function durationBetween(start, end) {
  const diff = parseTimeToMinutes(end) - parseTimeToMinutes(start);
  return diff > 0 ? diff : 60;
}

export const SCHEDULE_DURATION_PRESETS = [30, 60, 90, 120];

export function parseFlexibleTimeInput(value) {
  const raw = String(value || "").trim();
  if (!raw) return null;

  const colon = raw.match(/^(\d{1,2}):(\d{1,2})$/);
  if (colon) {
    const h = Number(colon[1]);
    const m = Number(colon[2]);
    if (h >= 0 && h <= 23 && m >= 0 && m <= 59) {
      return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
    }
    return null;
  }

  const digits = raw.replace(/\D/g, "");
  if (digits.length === 3 || digits.length === 4) {
    const m = Number(digits.slice(-2));
    const h = Number(digits.slice(0, -2));
    if (h >= 0 && h <= 23 && m >= 0 && m <= 59) {
      return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
    }
  }

  if (/^\d{1,2}$/.test(digits)) {
    const h = Number(digits);
    if (h >= 0 && h <= 23) {
      return `${String(h).padStart(2, "0")}:00`;
    }
  }

  return null;
}

export function formatDurationPresetLabel(minutes) {
  if (minutes === 30) return "30 мин";
  if (minutes === 60) return "1 ч";
  if (minutes === 90) return "1:30";
  if (minutes === 120) return "2 ч";
  if (minutes % 60 === 0) return `${minutes / 60} ч`;
  if (minutes > 60) {
    return `${Math.floor(minutes / 60)}:${String(minutes % 60).padStart(2, "0")}`;
  }
  return `${minutes} мин`;
}

function normalizeIsoDate(y, m, d) {
  if (!y || m < 1 || m > 12 || d < 1 || d > 31) return null;
  const dt = new Date(y, m - 1, d);
  if (
    dt.getFullYear() !== y ||
    dt.getMonth() !== m - 1 ||
    dt.getDate() !== d
  ) {
    return null;
  }
  return `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
}

/** Парсит дату: 2026-06-28, 28.06.2026, 28062026 */
export function parseFlexibleDateInput(value) {
  const raw = String(value || "").trim();
  if (!raw) return null;

  const iso = raw.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  if (iso) {
    return normalizeIsoDate(Number(iso[1]), Number(iso[2]), Number(iso[3]));
  }

  const dmy = raw.match(/^(\d{1,2})[./](\d{1,2})[./](\d{4})$/);
  if (dmy) {
    return normalizeIsoDate(Number(dmy[3]), Number(dmy[2]), Number(dmy[1]));
  }

  const digits = raw.replace(/\D/g, "");
  if (digits.length === 8) {
    const asIso = normalizeIsoDate(
      Number(digits.slice(0, 4)),
      Number(digits.slice(4, 6)),
      Number(digits.slice(6, 8)),
    );
    if (asIso) return asIso;
    return normalizeIsoDate(
      Number(digits.slice(4, 8)),
      Number(digits.slice(2, 4)),
      Number(digits.slice(0, 2)),
    );
  }

  return null;
}

export function formatDateDraft(iso) {
  if (!iso) return "";
  const [y, m, d] = String(iso).split("-");
  if (!y || !m || !d) return iso;
  return `${d}.${m}.${y}`;
}

export function normalizeTimeInput(value, fallback = "16:00") {
  return parseFlexibleTimeInput(value) || fallback;
}

export function normalizeDurationMin(value) {
  const n = Number(value);
  if (!Number.isFinite(n) || n < 1) return 60;
  return Math.min(600, Math.round(n));
}

export function scheduleEntryId(weekday, startTime) {
  return `${weekday}-${startTime}`;
}

export function slotsToEntries(slots) {
  return (slots || []).map((sl) => {
    const start = String(sl.start_time || "16:00").slice(0, 5);
    const end = String(sl.end_time || "").slice(0, 5);
    const duration_min = end
      ? durationBetween(start, end)
      : Number(sl.duration_min) || 60;
    return {
      id: scheduleEntryId(sl.weekday, start),
      weekday: Number(sl.weekday),
      start_time: start,
      duration_min,
      trainer_id: sl.trainer_id || null,
    };
  });
}

export function weekdayLabel(weekdayId) {
  return SCHEDULE_WEEKDAYS.find((d) => d.id === weekdayId)?.label || "?";
}

export function sortWeekdayIds(ids) {
  return [...ids].sort((a, b) => {
    const dayA = a === 0 ? 7 : a;
    const dayB = b === 0 ? 7 : b;
    return dayA - dayB;
  });
}

export function formatScheduleEntry(entry) {
  const day = weekdayLabel(entry.weekday);
  const end = addMinutesToTime(entry.start_time, entry.duration_min);
  return `${day}, ${entry.start_time}–${end} (${entry.duration_min} мин)`;
}

export function splitScheduleEntry(entry) {
  return {
    day: weekdayLabel(entry.weekday),
    start: entry.start_time,
    end: addMinutesToTime(entry.start_time, entry.duration_min),
    duration: entry.duration_min,
  };
}

export function formatDraftPreview(draftDays, startTime, durationMin) {
  if (!draftDays.length) return "";
  const days = sortWeekdayIds(draftDays).map(weekdayLabel).join(", ");
  const end = addMinutesToTime(startTime, durationMin);
  return `${days} · ${startTime}–${end} · ${durationMin} мин`;
}

export function sortScheduleEntries(entries) {
  return [...entries].sort((a, b) => {
    const dayA = a.weekday === 0 ? 7 : a.weekday;
    const dayB = b.weekday === 0 ? 7 : b.weekday;
    if (dayA !== dayB) return dayA - dayB;
    return parseTimeToMinutes(a.start_time) - parseTimeToMinutes(b.start_time);
  });
}

export function entryTimeRange(entry) {
  const start = parseTimeToMinutes(entry.start_time);
  const end = start + normalizeDurationMin(entry.duration_min);
  return { start, end };
}

export function scheduleEntriesOverlap(a, b) {
  if (Number(a.weekday) !== Number(b.weekday)) return false;
  const ra = entryTimeRange(a);
  const rb = entryTimeRange(b);
  return ra.start < rb.end && rb.start < ra.end;
}

export function trainerDisplayName(trainerId, trainers = []) {
  if (!trainerId) return null;
  const t = trainers.find((x) => String(x.id) === String(trainerId));
  return t?.full_name || t?.user?.full_name || "Другой тренер";
}

function conflictKey(entryId, otherTrainerId, otherStart) {
  return `${entryId}:${otherTrainerId || "none"}:${otherStart}`;
}

/**
 * Пересечения слотов текущего тренера с занятиями других тренеров в той же группе.
 */
export function findGroupTrainerSlotConflicts(
  entries,
  contextTrainerId,
  trainers = [],
) {
  if (!contextTrainerId || !entries?.length) return [];
  const ctx = String(contextTrainerId);
  const seen = new Set();
  const conflicts = [];

  for (const entry of entries) {
    const entryTrainer = entry.trainer_id ? String(entry.trainer_id) : null;
    if (!entryTrainer || entryTrainer !== ctx) continue;

    for (const other of entries) {
      if (other.id === entry.id) continue;
      const otherTrainerId = other.trainer_id
        ? String(other.trainer_id)
        : null;
      if (otherTrainerId === ctx) continue;
      if (!scheduleEntriesOverlap(entry, other)) continue;

      const key = conflictKey(entry.id, otherTrainerId, other.start_time);
      if (seen.has(key)) continue;
      seen.add(key);

      const own = splitScheduleEntry(entry);
      const foreign = splitScheduleEntry(other);
      conflicts.push({
        entryId: entry.id,
        label: `${own.day}, ${own.start}–${own.end}`,
        otherTrainerId,
        otherTrainerName: otherTrainerId
          ? trainerDisplayName(otherTrainerId, trainers)
          : null,
        otherSlotLabel: `${foreign.day}, ${foreign.start}–${foreign.end}`,
      });
    }
  }

  return conflicts;
}

/** Конфликт черновика (до добавления) с чужими слотами группы. */
export function findDraftTrainerSlotConflicts(
  draftDays,
  startTime,
  durationMin,
  entries,
  contextTrainerId,
  trainers = [],
) {
  if (!contextTrainerId || !draftDays?.length) return [];
  const ctx = String(contextTrainerId);
  const duration = normalizeDurationMin(durationMin);
  const time = normalizeTimeInput(startTime);
  const seen = new Set();
  const conflicts = [];

  for (const weekday of draftDays) {
    const draft = {
      id: `draft-${weekday}`,
      weekday,
      start_time: time,
      duration_min: duration,
    };

    for (const other of entries || []) {
      const otherTrainerId = other.trainer_id
        ? String(other.trainer_id)
        : null;
      if (otherTrainerId === ctx) continue;
      if (!scheduleEntriesOverlap(draft, other)) continue;

      const key = conflictKey(
        `draft-${weekday}`,
        otherTrainerId,
        other.start_time,
      );
      if (seen.has(key)) continue;
      seen.add(key);

      const foreign = splitScheduleEntry(other);
      conflicts.push({
        weekday,
        label: `${weekdayLabel(weekday)}, ${time}–${addMinutesToTime(time, duration)}`,
        otherTrainerId,
        otherTrainerName: otherTrainerId
          ? trainerDisplayName(otherTrainerId, trainers)
          : null,
        otherSlotLabel: `${foreign.day}, ${foreign.start}–${foreign.end}`,
      });
    }
  }

  return conflicts;
}

export function formatGroupTrainerConflictMessage(conflict) {
  if (conflict.otherTrainerName) {
    return `В это время у группы другой тренер — ${conflict.otherTrainerName} (${conflict.otherSlotLabel})`;
  }
  return `В это время у группы уже есть занятие (${conflict.otherSlotLabel})`;
}

export function groupTrainerConflictConfirmText(conflicts) {
  if (!conflicts.length) return "";
  const lines = conflicts.map((c) => `• ${formatGroupTrainerConflictMessage(c)}`);
  return (
    "Некоторые слоты пересекаются с занятиями других тренеров в этой группе:\n\n" +
    `${lines.join("\n")}\n\n` +
    "Сохранить расписание всё равно?"
  );
}

export function emptyScheduleDraft() {
  return {
    entries: [],
    draftDays: [],
    start_time: "16:00",
    duration_min: 60,
  };
}

/** Зал группы для отображения в онбординге */
export function groupBranchMeta(group, branches = []) {
  const id = group?.branch_id || group?.branch?.id || "";
  const name =
    group?.branch?.name ||
    branches.find((b) => b.id === id)?.name ||
    null;
  return { id, name: name || "Зал не указан" };
}

/** Сигнатура расписания для сравнения между группами */
export function scheduleEntriesSignature(entries) {
  return sortScheduleEntries(entries || [])
    .map(
      (e) =>
        `${e.weekday}:${String(e.start_time).slice(0, 5)}:${e.duration_min}`,
    )
    .join("|");
}

/**
 * Группы с одинаковым расписанием, но разными залами.
 * @returns {{ groups: { groupName: string, branchId: string, branchName: string }[], label: string }[]}
 */
export function findCrossVenueScheduleConflicts(
  groups,
  schedulesByGroupId,
  branches = [],
) {
  const buckets = new Map();

  for (const g of groups) {
    const entries = schedulesByGroupId[g.id]?.entries || [];
    if (!entries.length) continue;
    const sig = scheduleEntriesSignature(entries);
    const { id: branchId, name: branchName } = groupBranchMeta(g, branches);
    if (!buckets.has(sig)) buckets.set(sig, []);
    buckets.get(sig).push({
      groupName: g.name,
      branchId: branchId || branchName,
      branchName,
    });
  }

  const conflicts = [];
  for (const items of buckets.values()) {
    if (items.length < 2) continue;
    const branchKeys = new Set(items.map((i) => i.branchId));
    if (branchKeys.size < 2) continue;
    conflicts.push({
      groups: items,
      label: items
        .map((i) => `«${i.groupName}» — ${i.branchName}`)
        .join(", "),
    });
  }
  return conflicts;
}

export function crossVenueScheduleConfirmText(conflicts) {
  if (!conflicts.length) return "";
  const detail = conflicts
    .map((c) => `• ${c.label}`)
    .join("\n");
  return (
    "У нескольких групп в разных залах указано одно и то же время занятий:\n\n" +
    `${detail}\n\n` +
    "Тренировки не могут проходить в двух залах одновременно. Вы уверены, что хотите сохранить такое расписание?"
  );
}
